import moment, {Duration} from 'moment';
import React from 'react';
import {ITeamMember} from 'src/hooks/UseMembers';
import {VulnerabilitySeverity} from 'src/resources/TagResource';

export function getSeverityColor(severity: VulnerabilitySeverity) {
  switch (severity) {
    case VulnerabilitySeverity.Critical:
      return 'var(--pf-t--global--icon--color--severity--critical--default)';
    case VulnerabilitySeverity.High:
      return 'var(--pf-t--global--icon--color--severity--important--default)';
    case VulnerabilitySeverity.Medium:
      return 'var(--pf-t--global--icon--color--severity--moderate--default)';
    case VulnerabilitySeverity.Low:
      return 'var(--pf-t--global--icon--color--severity--minor--default)';
    case VulnerabilitySeverity.None:
      return 'var(--pf-t--global--icon--color--severity--none--default)';
    default:
      return 'var(--pf-t--global--icon--color--severity--undefined--default)';
  }
}

export function formatDate(
  date: string | number,
  timeStyle: 'short' | 'medium' | 'long' = 'short',
) {
  if (!date || date == -1) {
    return 'N/A';
  }

  const adjustedDate = typeof date === 'number' ? date * 1000 : date;
  return new Date(adjustedDate).toLocaleString(navigator.language, {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timeStyle,
    dateStyle: 'medium',
  });
}

/** Parse a date string to epoch millis, returning 0 for falsy or unparseable values. */
export function toEpochOrZero(value?: string): number {
  if (!value) return 0;
  const ts = Date.parse(value);
  return Number.isFinite(ts) ? ts : 0;
}

export function formatSize(sizeInBytes: number) {
  // Handle null/undefined but allow 0 as a valid value
  if (sizeInBytes === null || sizeInBytes === undefined) {
    return 'N/A';
  }

  // Handle 0 explicitly to avoid Math.log(0) = -Infinity
  if (sizeInBytes === 0) {
    return '0.00 KiB';
  }

  const i = Math.floor(Math.log(sizeInBytes) / Math.log(1024));
  return (
    (sizeInBytes / Math.pow(1024, i)).toFixed(2) +
    ' ' +
    ['B', 'KiB', 'MiB', 'GiB', 'TiB'][i]
  );
}

export function isValidEmail(email: string): boolean {
  const regex = /\S+@\S+\.\S+/;
  return regex.test(email);
}

export const validateTeamName = (name: string) => {
  return /^([a-z0-9]+(?:[._-][a-z0-9]+)*)$/.test(name);
};

export function parseRepoNameFromUrl(url: string): string {
  //url is in the format of <prefix>/repository/<org>/<repo>
  //or for nested repo: <prefix>/repository/<org>/<nested>/<repo>
  //or <prefix>/repository/<org>/<repo>/tag/<tag>
  const urlParts = url.split('/');
  const repoKeywordIndex = urlParts.indexOf('repository');
  if (repoKeywordIndex === -1) {
    return '';
  }

  let endIndex = urlParts.lastIndexOf('tag');
  if (endIndex === -1) {
    endIndex = urlParts.lastIndexOf('build');
  }

  if (endIndex === -1) {
    endIndex = urlParts.length;
  }
  // Taking nested repos into consideration
  return urlParts.slice(repoKeywordIndex + 2, endIndex).join('/');
}

export function parseOrgNameFromUrl(url: string): string {
  //url is in the format of <prefix>/repository/<org>/<repo> or <prefix>/organization/<org>/<repo>
  //or <prefix>/repository/<org>/<repo>/tag/<tag>
  const urlParts = url.split('/');
  const repoKeywordIndex = urlParts.indexOf('repository');
  const orgKeywordIndex = urlParts.indexOf('organization');
  if (repoKeywordIndex != -1) {
    return urlParts[repoKeywordIndex + 1];
  }

  if (orgKeywordIndex === -1) {
    return '';
  }

  return urlParts[orgKeywordIndex + 1];
}

export function parseTagNameFromUrl(url: string): string {
  //url is in the format of <prefix>/repository/<org>/<repo>
  //or <prefix>/repository/<org>/<repo>/tag/<tag>

  const urlParts = url.split('/');
  const repoKeywordIndex = urlParts.indexOf('repository');
  const tagKeywordIndex = urlParts.indexOf('tag');

  if (repoKeywordIndex === -1) {
    return '';
  }

  if (tagKeywordIndex === -1) {
    return '';
  }

  return urlParts[tagKeywordIndex + 1];
}

export function parseTeamNameFromUrl(url: string): string {
  //url is in the format of <prefix>/organization/<org>/teams/<team>
  const urlParts = url.split('/');
  const teamKeywordIndex = urlParts.indexOf('teams');
  if (teamKeywordIndex === -1) {
    return '';
  }
  return urlParts[teamKeywordIndex + 1];
}

export function parseTimeDuration(input: string): Duration {
  if (!input) {
    return moment.duration(NaN);
  }

  const durationRegex = /^(\d+)([smhdwy])$/i; // suffixes supported by Quay
  const match = input.match(durationRegex);

  if (!match) {
    return moment.duration(NaN);
  }

  const value = parseInt(match[1], 10);
  const unit = match[2];

  const duration = moment.duration(
    value,
    unit as moment.unitOfTime.DurationConstructor,
  );

  return duration;
}

export function humanizeTimeForExpiry(time_seconds: number | Duration): string {
  let duration;

  if (typeof time_seconds === 'number') {
    duration = moment.duration(time_seconds || 0, 'seconds');
  } else if (moment.isDuration(time_seconds)) {
    duration = time_seconds;
  } else {
    throw new Error('Invalid type for time_seconds');
  }
  const days = duration.asDays();

  if (Math.floor(days) < 31) {
    return duration.humanize();
  } else if (Math.floor(days) < 365) {
    const remainingDays = Math.floor(days % 31);
    const closeToAMonth =
      (remainingDays >= 0 && remainingDays < 7) ||
      (remainingDays > 24 && remainingDays < 31);
    return closeToAMonth ? duration.humanize() : `${Math.floor(days)} days`;
  } else {
    const years = Math.floor(days / 365);
    const remainingDays = Math.floor(days % 365);
    const months = Math.floor(remainingDays / 31);
    return months > 0 ? `${years} years ${months} months` : duration.humanize();
  }
}

export function getSeconds(duration_str: string): number {
  if (!duration_str) {
    return 0;
  }

  const [number, suffix] = duration_str.split('');
  return moment.duration(parseInt(number), suffix).asSeconds();
}

export function isNullOrUndefined(obj: any): boolean {
  return obj === null || obj === undefined;
}

export const getAccountTypeForMember = (member: ITeamMember): string => {
  if (member.is_robot) {
    return 'Robot account';
  } else if (!member.is_robot && !member.invited) {
    return 'Team member';
  } else if (member.invited) {
    return '(Invited)';
  }
};

export const titleCase = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const escapeHtmlString = function (text) {
  const textStr = (text || '').toString();
  const adjusted = textStr
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  return adjusted;
};

// Mirroring utility functions
const timeUnits = {
  seconds: 1,
  minutes: 60,
  hours: 60 * 60,
  days: 60 * 60 * 24,
  weeks: 60 * 60 * 24 * 7,
};

export const convertToSeconds = (value: number, unit: string): number => {
  return value * (timeUnits[unit] || 1);
};

export const convertFromSeconds = (
  seconds: number,
): {value: number; unit: string} => {
  const units = ['weeks', 'days', 'hours', 'minutes', 'seconds'];
  for (const unit of units) {
    const divisor = timeUnits[unit];
    if (seconds % divisor === 0) {
      return {value: seconds / divisor, unit};
    }
  }
  return {value: seconds, unit: 'seconds'};
};

// Convert ISO date to datetime-local format
export const formatDateForInput = (isoDate: string): string => {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export function formatRelativeTime(date: string | number): string {
  if (!date || date == -1 || date === 'Never') {
    return 'Never';
  }

  const adjustedDate = typeof date === 'number' ? date * 1000 : date;
  return moment(adjustedDate).fromNow();
}

/**
 * Recursively extracts plain text from a React node (JSX elements, fragments, etc.)
 * This is useful for making JSX content searchable in filters.
 *
 * @param node - A React node (string, number, element, fragment, array, etc.)
 * @returns The plain text content extracted from the React node
 *
 * @example
 * const jsx = <>Push of <code>tag123</code> to repository <code>user/repo</code></>;
 * extractTextFromReactNode(jsx); // Returns: "Push of tag123 to repository user/repo"
 */
export function extractTextFromReactNode(node: React.ReactNode): string {
  // Handle primitives (strings and numbers)
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }

  // Handle null, undefined, or boolean values
  if (node == null || typeof node === 'boolean') {
    return '';
  }

  // Handle arrays (e.g., from fragments or multiple children)
  if (Array.isArray(node)) {
    return node.map(extractTextFromReactNode).join('');
  }

  // Handle React elements (check if it's a valid React element)
  if (React.isValidElement(node)) {
    // Recursively extract text from children
    return extractTextFromReactNode(node.props.children);
  }

  // Fallback for unknown types
  return '';
}
